import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { N500, N50, R500 } from "@atlaskit/theme/colors";

import Button from "@/components/wpl/button/src";
import { wacToken } from "@/libraries/design/foundations";

import { useLocation } from "../../../utils";

const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

type ConsentConfig = {
    hasUserMadeChoiceOnMarketingCommunicationOptIn: boolean;
    locale: string;
    localeRequiresMarketingCommunicationOptIn: boolean;
    usersChoiceOnMarketingCommunicationOptIn: boolean;
};

export const AgileMailingListSignup = (props: {
    text: string;
    showEyebrow?: boolean;
}) => {
    const location = useLocation();
    const intl = useIntl();

    const emptyEmailError = intl.formatMessage({
        id: "AgileFooter.top.emptyEmailError",
        defaultMessage: "This field is required"
    });

    const invalidEmailError = intl.formatMessage({
        id: "AgileFooter.top.invalidEmailError",
        defaultMessage: "This field requires a valid email address"
    });

    const [errorMessage, setErrorMessage] = useState("");
    const [isValidatingEmail, setIsValidatingEmail] = useState(false);
    const [emailInput, setEmailInput] = useState("");
    const [honeypotInput, setHoneypotInput] = useState("");
    const [signupSuccess, setSignupSuccess] = useState(false);
    const [consentConfig, setConsentConfig] = useState<ConsentConfig>();

    useEffect(() => {
        if (isValidatingEmail) {
            if (emailInput === "") {
                setErrorMessage(emptyEmailError);
            } else {
                validateEmail(emailInput)
                    ? setErrorMessage("")
                    : setErrorMessage(invalidEmailError);
            }
        }
    }, [isValidatingEmail, emailInput, emptyEmailError, invalidEmailError]);

    useEffect(() => {
        getConsentConfig();
    }, []);

    const getConsentConfig = async () => {
        const stargateUrl = process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"];
        const consentConfigUrl = `${stargateUrl}/preferencesV2/consent-config`;

        await fetch(consentConfigUrl, {
            headers: {
                "Accept": "application/json",
                "Content-type": "application/json; charset=UTF-8"
            },
            method: "POST"
        })
            .then((response) => response.json())
            .then((data) => setConsentConfig(data))
            .catch(() => {
                console.error("Failed to fetch consent config");
            });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsValidatingEmail(true);

        if (honeypotInput) return;

        const stargateUrl = process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"];
        const postUrl = `${stargateUrl}/preferencesV2/update`;

        // These are hardcoded values that are required by the endpoint.
        // https://bitbucket.org/atlassian/magnolia-resources/annotate/f65b7a5bf31677bcde32caa3305364c7ffb88808/resources/js/components/generic/exact-target-formV2.ts?at=qa
        await fetch(postUrl, {
            headers: {
                "Accept": "application/json",
                "Content-type": "application/json; charset=UTF-8"
            },
            method: "POST",
            body: JSON.stringify({
                consentCheckboxValue: true,
                consentLabelText: "",
                email: emailInput,
                formId: "10c5c932-68d7-4db6-b1d1-959f4255d218",
                formUrl: location.href,
                locale: consentConfig?.locale || "UNKNOWN",
                privacyPolicyText: "",
                site: "atlassian"
            })
        })
            .then(() => setSignupSuccess(true))
            .catch(() => {
                setErrorMessage(
                    intl.formatMessage({
                        id: "AgileFooter.top.signupError",
                        defaultMessage: "An error occurred. Please try again."
                    })
                );
            });
    };

    return (
        <AgileMailingListSignupView
            {...props}
            errorMessage={errorMessage}
            setIsValidatingEmail={setIsValidatingEmail}
            emailInput={emailInput}
            setEmailInput={setEmailInput}
            setHoneypotInput={setHoneypotInput}
            signupSuccess={signupSuccess}
            handleSubmit={handleSubmit}
        />
    );
};

interface AgileMailingListSignupViewProps {
    text: string;
    showEyebrow?: boolean;
    errorMessage: string;
    setIsValidatingEmail: Dispatch<SetStateAction<boolean>>;
    emailInput: string;
    setEmailInput: Dispatch<SetStateAction<string>>;
    setHoneypotInput: Dispatch<SetStateAction<string>>;
    signupSuccess: boolean;
    handleSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
}

export const AgileMailingListSignupView = (
    props: AgileMailingListSignupViewProps
) => {
    const {
        text,
        showEyebrow,
        errorMessage,
        setIsValidatingEmail,
        emailInput,
        setEmailInput,
        setHoneypotInput,
        signupSuccess,
        handleSubmit
    } = props;

    const intl = useIntl();

    const subscribeText = intl.formatMessage({
        id: "AgileFooter.top.subscribe",
        defaultMessage: "Subscribe"
    });

    return (
        <div css={css({ gridArea: "signup" })}>
            {showEyebrow && (
                <div
                    css={css({
                        "color": N500,
                        "fontSize": "0.75rem",
                        "letterSpacing": "1px",
                        "lineHeight": "2",
                        "textTransform": "uppercase",
                        "order": "-1",
                        ":after": {
                            content: "",
                            display: "block",
                            borderBlockStart: "1px solid",
                            opacity: ".2",
                            width: "40px"
                        }
                    })}
                >
                    {subscribeText}
                </div>
            )}
            <p>{text}</p>
            <div>
                {!signupSuccess && (
                    <form onSubmit={handleSubmit}>
                        <div>
                            {/* Tech debt that should be fixed. See ldr/0012-jsx-a11y-strict.md for details. */}
                            {}
                            <label
                                htmlFor="email"
                                css={css({
                                    fontSize: ".875rem",
                                    color: N500
                                })}
                            >
                                <FormattedMessage
                                    id="AgileFooter.top.email"
                                    defaultMessage="Email"
                                />
                            </label>
                        </div>
                        <div css={css({ marginBlockEnd: "15px" })}>
                            <input
                                id="email"
                                type="email"
                                placeholder="email@example.com"
                                onChange={(e) => setEmailInput(e.target.value)}
                                onBlur={() =>
                                    emailInput && setIsValidatingEmail(true)
                                }
                                aria-invalid={errorMessage ? true : undefined}
                                css={css({
                                    backgroundColor: wacToken(
                                        "color.neutral.light0"
                                    ),
                                    border: `1px solid ${N50}`,
                                    borderRadius: "3px",
                                    display: "inline-block",
                                    fontFamily: wacToken("font.charlie.text"),
                                    fontSize: ".875rem",
                                    lineHeight: "1.71428571",
                                    height: "40px",
                                    marginInlineEnd: "8px",
                                    maxWidth: "240px",
                                    outline: 0,
                                    padding: "0 12px",
                                    width: "100%"
                                })}
                            />
                            {errorMessage && (
                                <label
                                    htmlFor="email"
                                    css={css({
                                        display: "block",
                                        fontSize: ".875rem",
                                        color: R500,
                                        margin: "12px 0"
                                    })}
                                >
                                    {errorMessage}
                                </label>
                            )}
                        </div>
                        {/* honeypot */}
                        <div css={css({ display: "none" })}>
                            <input
                                type="text"
                                name="list name"
                                aria-label="keep this field blank"
                                onChange={(e) =>
                                    setHoneypotInput(e.target.value)
                                }
                            />
                        </div>
                        <div>
                            <Button
                                type="secondary"
                                text={subscribeText}
                                inputType="submit"
                                dataEventProps={{
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    "data-product-key": "jiraSoftware",
                                    "data-event-container": "exactTargetForm"
                                }}
                                aria-label={subscribeText}
                            />
                        </div>
                    </form>
                )}
                {signupSuccess && (
                    <p>
                        <FormattedMessage
                            id="AgileFooter.top.signup-success"
                            defaultMessage="Thanks for signing up!"
                        />
                    </p>
                )}
            </div>
        </div>
    );
};

export default AgileMailingListSignup;
